import React, { useEffect, useState } from "react";
import { View, Text, StyleSheet, ScrollView } from "react-native";

import {
  BG_STYLES,
  BORDER_STYLES,
  Button,
  BUTTON_STATUS,
  BUTTON_TYPE,
  COLORS,
  COMMON_STYLES,
  FloatingLabelInput,
  SIZE,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";

import FormTextField from "app/components/reusable/FormTextField";
import DropdownInput from "app/components/reusable/Dropdown";
import BigInput from "app/components/reusable/BigInput";
import FormRadioField from "app/components/reusable/FormRadioField";
import ToastAndroid from "app/mocks/ToastAndroid";
import { rnNavigation } from "app/utils/appConfig";

import updateFarmerDetails from "../../apis/updateFamerDetails";
import { ROUTES } from "../../routes/types";
import { backHandler, DIMENSION_WIDTH } from "app/utils/common";

import fetchLocationDetails from "../../apis/fetchLocationDetails";
import getVillagesByBlock from "../../apis/getVillagesByBlock";
import getBlocksByDistrict from "../../apis/getBlocksByDistrict";
import { AREA_UNITS, GENDER } from "../../constants/common";
import { Address, LAND_UNITS } from "../../models/farmer";
import { validateDigitOnly } from "../../utils/address";
import addFarmerDetails from "../../apis/addFamerDetails";
import { trackEvent } from "../../utils/tracking";
import { Farmer360UserInformationAdded } from "../../utils/event";

export type Option = { id: string; value: string };

interface Props {
  farmerId?: string;
  phoneNumber: string;
  name: string;
  age?: string;
  gender?: GENDER;
  isSmartPhoneOwner?: boolean;
  isDairyFarm?: boolean;
  address?: Address;
  farmingArea: string | null;
  landAreaUnit: string | null;
}

const PersonalInfoForm = ({
  farmerId,
  phoneNumber,
  name,
  age,
  gender,
  isSmartPhoneOwner,
  isDairyFarm,
  address,
  farmingArea,
  landAreaUnit,
}: Props) => {
  const [blocks, setBlocks] = useState<Option[]>([]);
  const [villages, setVillages] = useState<Option[]>([]);

  const [loading, setLoading] = useState({
    location: false,
    address: false,
    updating: false,
    blocks: false,
    villages: false,
  });

  const [error, setError] = useState({
    name: false,
    gender: false,
    pincode: false,
    phoneNumber: false,
    farmingArea: false,
  });

  const [data, setData] = useState({
    phoneNumber,
    name,
    age,
    gender,
    isSmartPhoneOwner,
    isDairyFarm,
    farmingArea,
    landAreaUnit: landAreaUnit || LAND_UNITS.ACRES,
    address,
  });

  const isErrorInForm =
    error.name ||
    error.gender ||
    error.pincode ||
    error.phoneNumber ||
    !data.name ||
    !data.phoneNumber ||
    !data.address?.pin_code ||
    !data.farmingArea;

  const isDataLoading = Object.values(loading).some((value) => value);

  const updateState = (name: string, value: any) => {
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const updateError = (name: string, value: any) => {
    setError((prev) => ({ ...prev, [name]: value }));
  };

  const fetchAndUpdateVillageList = async (blockId: string) => {
    setLoading((prev) => ({ ...prev, villages: true }));
    const data = await getVillagesByBlock(blockId);
    setVillages(data.map((v) => ({ id: v.id.toString(), value: v.name })));
    setLoading((prev) => ({ ...prev, villages: false }));
  };

  const fetchAndUpdateBlockList = async (districtId: string) => {
    setLoading((prev) => ({ ...prev, blocks: true }));
    const data = await getBlocksByDistrict(districtId);
    setBlocks(data.map((b) => ({ id: b.id.toString(), value: b.name })));
    setLoading((prev) => ({ ...prev, blocks: false }));
  };

  const handlePincodeChange = (pincode: string) => {
    if (pincode.length === 6) {
      fetchAndUpdateLocation(pincode);
    } else {
      updateState("address", { pin_code: pincode });
      updateError("pincode", true);
    }
  };

  const fetchAndUpdateLocation = async (pincode: string) => {
    setLoading((prev) => ({ ...prev, location: true }));
    let addressObj: Address = { pin_code: pincode };
    let error = true;
    const locationInfo = await fetchLocationDetails(pincode);
    if (locationInfo) {
      addressObj = {
        pin_code: pincode,
        state: locationInfo.state,
        state_id: locationInfo.kheti_state_id,
        district: locationInfo.district,
        district_id: locationInfo.kheti_district_id,
        block: locationInfo.block,
        block_id: locationInfo.kheti_block_id,
      };
      error = false;
    }
    updateState("address", addressObj);
    updateError("pincode", error);
    setLoading((prev) => ({ ...prev, location: false }));
  };

  const onSubmit = async () => {
    if (!data.farmingArea) {
      updateError("farmingArea", true);
      return;
    }

    if (!data.gender) {
      updateError("gender", true);
      return;
    }

    if (isErrorInForm) {
      return;
    }

    setLoading((prev) => ({ ...prev, updating: true }));
    const requestBody = {
      full_name: data.name,
      phone_number: data.phoneNumber,
      profile: {
        age: data.age,
        gender: data.gender,
        smartphone_user: data.isSmartPhoneOwner,
        total_leased_land: data.farmingArea,
        total_leased_land_unit: data.landAreaUnit,
        dairy_farmer: data.isDairyFarm,
      },
      address: data.address,
    };
    if (farmerId) {
      const success = await updateFarmerDetails(farmerId, requestBody);
      if (success) {
        ToastAndroid.show(
          "Farmer details updated successfully",
          ToastAndroid.SHORT,
        );
        trackEvent(Farmer360UserInformationAdded, {
          farmername: data.name,
          farmermobile: data.phoneNumber,
          InformationType: "farmerprofile",
          InformationUpdated: "profileinfo", // add diff checker logic
        });
        rnNavigation({
          name: ROUTES.FARMER_360_HOME,
          params: { farmerId, name: data.name },
          replace: true,
        });
      }
    } else {
      const response = await addFarmerDetails(requestBody);
      if (response.user) {
        ToastAndroid.show(
          response?.message || "Farmer Created Successfully.",
          ToastAndroid.SHORT,
        );

        trackEvent(Farmer360UserInformationAdded, {
          farmername: data.name,
          farmermobile: data.phoneNumber,
          InformationType: "farmerprofile",
          InformationUpdated: "newfarmercreated",
        });

        rnNavigation({
          name: ROUTES.FARMER_360_HOME,
          params: { farmerId: response.user.id },
          replace: true,
        });
      } else {
        ToastAndroid.show(response.message, ToastAndroid.SHORT);
      }
    }

    setLoading((prev) => ({ ...prev, updating: false }));
  };

  useEffect(() => {
    if (address?.pin_code) {
      updateState("address", { ...address });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.pin_code]);

  useEffect(() => {
    if (data.address?.district_id) {
      fetchAndUpdateBlockList(data.address.district_id);
    } else {
      setBlocks([]);
    }
  }, [data.address?.district_id]);

  useEffect(() => {
    if (data.address?.block_id) {
      fetchAndUpdateVillageList(data.address.block_id);
    } else {
      setVillages([]);
    }
  }, [data.address?.block_id]);

  return (
    <ScrollView
      contentContainerStyle={[
        COMMON_STYLES.fullWidth,
        BG_STYLES.bgNeutral10,
        COMMON_STYLES.alignItemsCenter,
      ]}
    >
      {isDataLoading && <Spinner type="on-screen" />}
      <View
        style={[
          BG_STYLES.bgWhite,
          COMMON_STYLES.alignItemsCenter,
          styles["pv5/2"],
          styles.container,
          COMMON_STYLES.ph1,
          styles.sectionDivider,
        ]}
      >
        <View style={styles.section}>
          <Text
            style={[
              TYPOGRAPHY.HEADING3,
              VARIANTS_STYLES.MEDIUM,
              TEXT_STYLES.textNeutral90,
            ]}
          >
            Add Farmer Details
          </Text>
          <BigInput
            title={"Farmer's Phone Number"}
            placeholder={"Enter phone number"}
            value={data.phoneNumber}
            onChangeText={(text: string) => {
              if (validateDigitOnly(text) && text.length <= 10) {
                updateState("phoneNumber", text);
                updateError("phoneNumber", text.length !== 10);
              }
            }}
            leftIcon={
              <Text
                style={[
                  TYPOGRAPHY.HEADING3,
                  VARIANTS_STYLES.MEDIUM,
                  TEXT_STYLES.textNeutral90,
                ]}
              >
                +91
              </Text>
            }
            disabled={!!farmerId}
            style={[
              COMMON_STYLES["mt5/4"],
              COMMON_STYLES["br1/2"],
              error.phoneNumber && BORDER_STYLES.borderError100,
            ]}
            leftIconContainerStyle={[
              styles.leftIconStyle,
              styles.leftTopBottomBorderRadius,
            ]}
            inputStyle={[
              COMMON_STYLES.p0,
              !!farmerId && TEXT_STYLES.textNeutral40,
              TYPOGRAPHY.HEADING3,
              VARIANTS_STYLES.MEDIUM,
            ]}
            labelStyle={!!farmerId && TEXT_STYLES.textNeutral40}
            maxLength={10}
          />
          <Text
            style={[
              TYPOGRAPHY.CAPTION1,
              VARIANTS_STYLES.BASE,
              TEXT_STYLES.textNeutral70,
              COMMON_STYLES.ml1,
              COMMON_STYLES["mt1/4"],
            ]}
          >
            Use your 10-digits mobile number
          </Text>

          <FloatingLabelInput
            title="Farmer’s Name*"
            titleFocusedStyle={TEXT_STYLES.textSuccess100}
            value={data.name}
            containerStyle={COMMON_STYLES["mt3/2"]}
            onChangeText={(text: string) => {
              updateState("name", text);
              updateError("name", text === "");
            }}
            error={error.name}
            errorText={"This field is mandatory"}
          />

          <BigInput
            title={"Age"}
            placeholder={"Age"}
            value={data.age ? data.age.toString() : ""}
            onChangeText={(age: string) => {
              if (isNaN(Number(age))) return;
              if (Number(age) < 0 || Number(age) > 99) return;
              updateState("age", age);
            }}
            rightIcon={
              <Text
                style={[
                  TEXT_STYLES.textNeutral90,
                  VARIANTS_STYLES.MEDIUM,
                  TYPOGRAPHY.HEADING3,
                ]}
              >
                years old
              </Text>
            }
            rightIconContainerStyle={[
              COMMON_STYLES.ph1,
              styles.max30,
              styles.rightTopBottomBorderRadius,
            ]}
            style={[COMMON_STYLES["mt3/2"], COMMON_STYLES["br1/2"]]}
            inputStyle={[COMMON_STYLES.p0, COMMON_STYLES["mt1/8"]]}
          />

          <FormRadioField
            title="Gender*"
            onChange={(isMaleSelected: boolean) => {
              updateState(
                "gender",
                isMaleSelected ? GENDER.MALE : GENDER.FEMALE,
              );
              updateError("gender", false);
            }}
            labelNo={GENDER.FEMALE}
            labelYes={GENDER.MALE}
            style={COMMON_STYLES["mt3/2"]}
            value={
              data.gender !== undefined
                ? data.gender === GENDER.MALE
                : undefined
            }
            titleStyle={error.gender && TEXT_STYLES.textError100}
          />
          {error.gender && (
            <Text
              style={[
                TYPOGRAPHY.CAPTION1,
                VARIANTS_STYLES.BASE,
                TEXT_STYLES.textError100,
                COMMON_STYLES["mt1/8"],
              ]}
            >
              Please select gender
            </Text>
          )}

          <FormRadioField
            title="Farmer owns a smart phone?"
            onChange={(isYesSelected) =>
              updateState("isSmartPhoneOwner", isYesSelected)
            }
            style={COMMON_STYLES["mt3/2"]}
            titleStyle={[
              TYPOGRAPHY.TEXT2,
              VARIANTS_STYLES.MEDIUM,
              TEXT_STYLES.textNeutral80,
            ]}
            value={data.isSmartPhoneOwner}
          />
        </View>
      </View>
      <View
        style={[
          BG_STYLES.bgWhite,
          COMMON_STYLES.alignItemsCenter,
          styles["pv5/2"],
          styles.container,
          COMMON_STYLES.ph1,
          styles.sectionDivider,
        ]}
      >
        <View style={styles.section}>
          <FormRadioField
            title="Is farmer a Dairy Farmer?"
            onChange={(isYesSelected) =>
              updateState("isDairyFarm", isYesSelected)
            }
            titleStyle={[
              TYPOGRAPHY.TEXT2,
              VARIANTS_STYLES.MEDIUM,
              TEXT_STYLES.textNeutral80,
            ]}
            value={data.isDairyFarm}
          />
        </View>
      </View>
      <View
        style={[
          BG_STYLES.bgWhite,
          COMMON_STYLES.alignItemsCenter,
          styles["pv5/2"],
          styles.container,
          COMMON_STYLES.ph1,
          styles.sectionDivider,
        ]}
      >
        <View style={styles.section}>
          <Text
            style={[
              TYPOGRAPHY.HEADING3,
              VARIANTS_STYLES.MEDIUM,
              TEXT_STYLES.textNeutral90,
            ]}
          >
            Farming Area
          </Text>
          <View
            style={[COMMON_STYLES.row, COMMON_STYLES.flex1, COMMON_STYLES.mt1]}
          >
            <FormTextField
              value={data.farmingArea || ""}
              placeholder={"Total Land Area*"}
              keyboardType="numeric"
              onChangeText={(area: string) => {
                if (isNaN(Number(area))) return;
                updateState("farmingArea", area);
                updateError("farmingArea", false);
              }}
              containerStyle={COMMON_STYLES.flex1}
              style={{
                paddingRight: 0,
                overflow: "hidden",
              }}
              rightLabelIcon={
                <Text
                  style={[
                    BG_STYLES.bgNeutral00,
                    TYPOGRAPHY.HEADING3,
                    VARIANTS_STYLES.BASE,
                    TEXT_STYLES.textNeutral70,
                    COMMON_STYLES["p3/4"],
                    BORDER_STYLES.borderNeutral00,
                    COMMON_STYLES.b1,
                    COMMON_STYLES["br1/2"],
                    { borderRadius: 0, gap: 8 },
                  ]}
                >
                  {AREA_UNITS[0].value}
                </Text>
              }
              error={error["farmingArea"]}
              errorText={"This field is mandatory"}
            />
          </View>
        </View>
      </View>
      <View
        style={[
          BG_STYLES.bgWhite,
          COMMON_STYLES.alignItemsCenter,
          styles["pv5/2"],
          styles.container,
          COMMON_STYLES.ph1,
          styles.sectionDivider,
        ]}
      >
        <View style={styles.section}>
          <Text
            style={[
              TYPOGRAPHY.HEADING3,
              VARIANTS_STYLES.MEDIUM,
              TEXT_STYLES.textNeutral90,
            ]}
          >
            Address
          </Text>
          <FloatingLabelInput
            title="Pin Code*"
            titleFocusedStyle={TEXT_STYLES.textSuccess100}
            value={data.address?.pin_code || ""}
            containerStyle={COMMON_STYLES["mt1"]}
            onChangeText={handlePincodeChange}
            error={error.pincode}
            errorText={"Please enter a valid pincode"}
          />
          <FloatingLabelInput
            title="State*"
            titleFocusedStyle={TEXT_STYLES.textSuccess100}
            value={data.address?.state || ""}
            containerStyle={COMMON_STYLES["mt1"]}
            isDisable
            isInputEditable={false}
          />
          <FloatingLabelInput
            title="District*"
            titleFocusedStyle={TEXT_STYLES.textSuccess100}
            value={data.address?.district || ""}
            containerStyle={COMMON_STYLES["mt1"]}
            isDisable
            isInputEditable={false}
          />
          <DropdownInput
            value={data.address?.block_id?.toString() || ""}
            placeholder={"Block*"}
            onChange={(block: string) => {
              updateState("address", {
                ...data.address,
                block_id: block,
                village_id: "",
                village: "",
              });
            }}
            options={blocks}
            containerStyle={COMMON_STYLES["mt1"]}
            isDisabled={!data.address?.district_id}
            modalStyle={[styles.selectDropdown, styles.container]}
          />
          <DropdownInput
            value={data.address?.village_id?.toString() || ""}
            placeholder={"Village"}
            onChange={(village: string) => {
              updateState("address", {
                ...data.address,
                village_id: village,
              });
            }}
            options={villages}
            containerStyle={COMMON_STYLES["mt1"]}
            isDisabled={!data.address?.block_id}
            modalStyle={[styles.selectDropdown, styles.container]}
          />
        </View>
      </View>

      <View
        style={[
          BG_STYLES.bgWhite,
          COMMON_STYLES.alignItemsCenter,
          COMMON_STYLES.row,
          styles.container,
          COMMON_STYLES.ph1,
          styles.sectionDivider,
          styles.actionButtonContainer,
        ]}
      >
        <Button
          onClick={backHandler}
          text={"Cancel"}
          buttonStyle={[COMMON_STYLES.flex1, COMMON_STYLES["mr0.5"]]}
          type={BUTTON_TYPE.SECONDARY}
          size={SIZE.large}
        />
        <Button
          onClick={onSubmit}
          text={"Submit"}
          buttonStyle={[COMMON_STYLES.flex1, COMMON_STYLES["ml0.5"]]}
          size={SIZE.large}
          status={BUTTON_STATUS.ACTIVE}
        />
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  leftIconStyle: {
    paddingLeft: 20,
    paddingRight: 16,
    backgroundColor: COLORS.neutral10,
  },
  "pv5/2": {
    paddingVertical: 40,
  },
  container: { maxWidth: 836, width: "100%" },
  section: { maxWidth: 408, width: "100%" },
  max30: {
    maxWidth: "30%",
  },
  leftTopBottomBorderRadius: {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  rightTopBottomBorderRadius: {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
  sectionDivider: {
    borderBottomWidth: 1,
    borderColor: COLORS.neutral20,
  },
  actionButtonContainer: {
    paddingHorizontal: 40,
    paddingVertical: 24,
  },
  selectDropdown: {
    left: DIMENSION_WIDTH > 836 ? (DIMENSION_WIDTH - 836) / 2 : 0,
  },
});

export default PersonalInfoForm;
